// src/utils/attribution.js
// Captures and persists trial-signup attribution across a browser session:
// referral code, Facebook click id (fbclid), external referrer, and the first
// path the visitor landed on. Persisted in sessionStorage so it survives the
// /t/:code -> /trial redirect and any navigation in between.
//
// The stored object mirrors the fields of the Phase 0 trials.attribution object
// that originate from the browser:
//   { code, fbclid, referrer, landingPath }
// The rest of trials.attribution is filled elsewhere and MUST NOT be set here:
//   - codeType            -> resolved server-side by createTrialSignup
//   - heardAbout /
//     heardAboutOther     -> collected on the /trial form
//
// Hard rule: attribution is best-effort and must NEVER block or break a signup.
// Every function here swallows its own errors and degrades to null.

const KEY = 'trial_attribution';

function read() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
}

function write(obj) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    /* private mode / storage disabled — best-effort only */
  }
}

// Same slug rule as ReferralCodeManager.slugify. referralCodes doc ids ARE the
// slug, so /t/:code params and ?ref= values must be normalised identically to
// resolve against a real doc.
export function normalizeReferralCode(raw) {
  return (raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Call on every entry point to the trial flow (/t/:code and /trial). First write
// wins for landingPath / referrer / fbclid, so later navigation within the flow
// can't overwrite the original source. Returns the merged object. Never throws.
export function captureAttribution() {
  const existing = read();
  const next = { ...existing };

  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams('');
  }

  if (next.landingPath == null) {
    next.landingPath = window.location.pathname + window.location.search;
  }

  if (next.referrer === undefined) {
    const ref = document.referrer || '';
    next.referrer =
      ref && !ref.startsWith(window.location.origin) ? ref : null;
  }

  const fbclid = params.get('fbclid');
  if (fbclid && next.fbclid == null) {
    next.fbclid = fbclid;
  }

  // ?ref= / ?code= on a direct /trial hit. The /t/:code path uses
  // setAttributionCode instead, after it has validated the code.
  const qsCode = params.get('ref') || params.get('code');
  if (qsCode && next.code == null) {
    next.code = normalizeReferralCode(qsCode) || null;
  }

  write(next);
  return next;
}

// Set by TrialLanding once it has resolved /t/:code against referralCodes.
// Pass the validated slug, or null for an unknown / inactive code.
export function setAttributionCode(code) {
  write({ ...read(), code: code || null });
}

// The browser-originated attribution fields, shaped for trials.attribution.
// Missing values are null, never undefined.
export function getAttribution() {
  const a = read();
  return {
    code: a.code || null,
    fbclid: a.fbclid || null,
    referrer: a.referrer || null,
    landingPath: a.landingPath || null,
  };
}

// Call after a trial signup succeeds so a later unrelated visit in the same tab
// doesn't inherit a stale code.
export function clearAttribution() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* no-op */
  }
}
